import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, MessageCircle, Shield, Sparkles, Droplets, Wrench } from 'lucide-react';

const WA_URL = 'https://wa.me/905414220278?text=Merhaba,%20Art%20Garage%20hakkında%20bilgi%20almak%20istiyorum.';

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-background text-foreground min-h-screen font-body relative">
      {/* Top Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/70 hairline-b">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <img src="/art-garage-logo.png" alt="ART Garage" className="h-8 w-auto" />
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/login')}
              className="cta-outline px-4 h-9 rounded-lg text-sm"
              data-testid="home-login-btn"
            >
              Giris Yap
            </button>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-brand px-4 h-9 rounded-lg text-sm hidden sm:flex items-center gap-1.5"
            >
              <MessageCircle size={14} /> WhatsApp
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="grid-bg relative">
        <div className="max-w-6xl mx-auto px-5 py-14 md:py-20 text-center">
          <div className="mb-8 flex justify-center fade-up">
            <img
              src="/art-garage-logo.png"
              alt="ART Garage"
              className="h-28 md:h-36 w-auto logo-float"
            />
          </div>

          <div
            className="inline-flex items-center gap-2 mb-7 px-3 py-1.5 rounded-full hairline-strong fade-up"
            style={{ animationDelay: '100ms', background: 'hsl(0 0% 7%)' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-brand pulse-dot" />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Izmir . Est. 2010
            </span>
          </div>

          <h1
            className="font-display font-extrabold leading-[0.9] tracking-[-0.04em] fade-up"
            style={{ animationDelay: '160ms' }}
          >
            <span className="block text-4xl md:text-6xl lg:text-7xl">
              Aracin hak ettigi
            </span>
            <span
              className="block text-5xl md:text-7xl lg:text-8xl mt-2 italic"
              style={{
                background: 'linear-gradient(180deg, hsl(var(--brand)) 0%, hsl(var(--brand) / 0.7) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              profesyonel dokunus.
            </span>
          </h1>

          <p
            className="mt-8 max-w-xl mx-auto text-muted-foreground text-base md:text-lg leading-relaxed fade-up"
            style={{ animationDelay: '220ms' }}
          >
            PPF, renkli kaplama, seramik ve detailing. Markali urunler, sertifikali ustalar,
            <span className="text-foreground"> uzun donem garanti.</span>
          </p>

          <div
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 fade-up"
            style={{ animationDelay: '280ms' }}
          >
            <button
              onClick={() => navigate('/login')}
              className="cta-brand px-6 h-12 rounded-lg inline-flex items-center gap-2"
              data-testid="hero-cta-btn"
            >
              Hemen Basla <ArrowRight size={16} />
            </button>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="cta-outline px-6 h-12 rounded-lg inline-flex items-center gap-2"
            >
              <MessageCircle size={16} /> WhatsApp ile Iletisim
            </a>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-5">
          <div className="mb-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand mb-2">
              -- Hizmetlerimiz
            </div>
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
              Her detayda<br />
              <span className="text-muted-foreground">ustalik vardir.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { icon: Shield,   n: '01', t: 'PPF -- Boya Koruma', d: 'Tas darbelerine karsi 10 yila uzayan koruma. Self-healing teknolojisi ile gorunmez garanti.' },
              { icon: Sparkles, n: '02', t: 'Renkli Kaplama',     d: 'Full wrap ya da kismi renk donusumu. Mat, parlak, satin -- geri donusu olan ozgurluk.' },
              { icon: Droplets, n: '03', t: 'Seramik Kaplama',    d: '9H sertlik, UV direnci, kimyasal bariyer. Fabrika parlakligindan oteye.' },
              { icon: Wrench,   n: '04', t: 'Detailing',          d: 'Ic ve dis detayli bakim, kokulu temizlik, motor bolmesi. Arac yeniden teslim havasi.' },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.n}
                  className="card-premium rounded-2xl p-6 md:p-7 group cursor-pointer fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center group-hover:text-brand transition-colors">
                      <Icon size={18} />
                    </div>
                    <span className="font-mono text-[10px] text-muted-foreground">{s.n}</span>
                  </div>
                  <h3 className="font-display font-semibold text-xl mb-2 tracking-tight">{s.t}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-5">
          <div className="card-glow rounded-3xl p-10 md:p-14 text-center relative overflow-hidden">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand mb-3">
              -- Randevu
            </div>
            <h3 className="font-display font-bold text-3xl md:text-5xl tracking-tight leading-tight mb-4">
              Aracini ne zaman<br />bize emanet edeceksin?
            </h3>
            <p className="text-muted-foreground text-base md:text-lg max-w-md mx-auto mb-8">
              Ilk gorusmede arac durumunu birlikte degerlendiriyor, sana ozel bir plan cikariyoruz.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="cta-brand px-7 h-12 rounded-lg inline-flex items-center gap-2"
            >
              Randevu Olustur <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="hairline-t py-8">
        <div className="max-w-6xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/art-garage-logo.png" alt="ART Garage" className="h-6 w-auto" />
            <span className="font-mono text-xs text-muted-foreground">&copy; 2026 . IZMIR</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
